import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { DEFAULT_IMAGE_WIDTH } from './constants';
import styles from './image.module.scss';

interface IProps {
  width?: number;
  maxWidth?: number;
  filename?: string;
  filenameTablet?: string; // If the tablet image is different one
  filenameMobile?: string; // If the mobile image is different one
  src?: string; // When want to use whole image url
  alt?: string;
  height?: number;
  className?: string;
  hideOnDesktop?: boolean;
  hideOnTablet?: boolean;
  hideOnMobile?: boolean;
  hideBiggerThenMobile?: boolean;
  noBlurLoading?: boolean; // When we dont want to have this blurry image first
}

const Image = ({
  filename,
  filenameTablet,
  filenameMobile,
  src,
  width = DEFAULT_IMAGE_WIDTH,
  maxWidth,
  height,
  alt,
  className = '',
  hideOnDesktop,
  hideOnTablet,
  hideOnMobile,
  hideBiggerThenMobile,
  noBlurLoading,
}: IProps) => {
  const [loadedSrc, setLoadedSrc] = useState(noBlurLoading ? true : false);
  const isDesktop = useMediaQuery({
    query: '(min-width: 992px)',
  });
  const isTablet = useMediaQuery({
    query: '(max-width: 991px) and (min-width: 768px)',
  });
  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  if (hideOnDesktop && isDesktop) return null;
  if (hideOnTablet && isTablet) return null;
  if (hideBiggerThenMobile && !isMobile) return null;
  if (hideOnMobile && isMobile) return null;

  const getImageFilename = () => {
    if (isMobile && filenameMobile) {
      return filenameMobile;
    }

    if (isTablet && filenameTablet) {
      return filenameTablet;
    }

    return filename;
  };

  const getImageSize = () => {
    let imageSize = width > 767 ? 767 : width;

    if (isTablet) {
      imageSize = width > 992 ? 992 : width;
    }

    if (isDesktop) {
      imageSize = width > 1280 ? 1280 : width;
    }

    return imageSize;
  };

  const getImageSizeRatio = (imageSize: number, ratio: number) => {
    const max = maxWidth ?? DEFAULT_IMAGE_WIDTH;
    const imageSizeRatio = imageSize * ratio;

    return imageSizeRatio > max ? max : imageSizeRatio;
  };

  const pixelization = 100;
  const imgPath = src || `${process.env.GATSBY_MEDIA_URL}${getImageFilename()}`;
  const imageWidth = getImageSize();
  const onImageLoad = () => setLoadedSrc(true);

  if (src) {
    return (
      <img
        data-testid="image"
        style={{ display: 'inline-block', maxHeight: height ? `${height}px` : 'auto', ...(height ? { objectFit: 'cover' } : {}) }}
        width={imageWidth}
        height={height}
        className={className}
        src={src}
        alt={alt}
        decoding="async"
      />
    );
  }

  return (
    <>
      <img
        style={!loadedSrc ? { display: 'block' } : { display: 'none' }}
        className={styles.blur}
        width={imageWidth}
        src={`${imgPath}?w=${pixelization}`}
        data-testid="image-placeholder"
        alt={alt}
      />
      <img
        data-testid="image"
        style={
          loadedSrc
            ? { display: 'inline-block', maxHeight: height ? `${height}px` : 'auto', ...(height ? { objectFit: 'cover' } : {}) }
            : { display: 'none' }
        }
        width={imageWidth}
        height={height}
        onLoad={onImageLoad}
        className={className}
        src={`${imgPath}?w=${imageWidth}`}
        srcSet={`
        ${imgPath}?w=${imageWidth},
        ${imgPath}?w=${getImageSizeRatio(imageWidth, 2)} 2x,
        ${imgPath}?w=${getImageSizeRatio(imageWidth, 3)} 3x
      `}
        sizes={`
        (max-width: 420px) 420px,
        (max-width: 768px) 768px,
        (max-width: 1280px) 1280px,
        ${imageWidth}px
      `}
        alt={alt}
        decoding="async"
      />
    </>
  );
};

export default Image;
